(() => {
  'use strict';

  const initialized = new WeakSet();
  const transitionDuration = 380;

  const initDrawer = (root) => {
    if (!root || initialized.has(root)) return;

    const drawerId = root.dataset.vccSharedTrustRoot;
    const panel = root.querySelector('[data-vcc-shared-trust-panel]');
    if (!drawerId || !panel) return;

    initialized.add(root);

    let lastOpener = null;
    let closeTimer = 0;
    let ownsHistoryEntry = false;
    let backgroundState = [];
    let openGeneration = 0;
    const historyStateKey = 'vccSharedTrustDrawer';

    const openers = () => Array.from(document.querySelectorAll('[data-vcc-shared-trust-open]'))
      .filter((opener) => opener.dataset.vccSharedTrustOpen === drawerId);

    const setExpanded = (expanded) => {
      openers().forEach((opener) => opener.setAttribute('aria-expanded', String(expanded)));
    };

    const focusableElements = () => Array.from(panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((element) => !element.hidden && element.offsetParent !== null);

    const isolateBackground = () => {
      if (backgroundState.length) return;
      const nextState = [];
      const isolate = (element) => {
        nextState.push({
          element,
          inert: element.inert,
          ariaHidden: element.getAttribute('aria-hidden')
        });
        element.inert = true;
        element.setAttribute('aria-hidden', 'true');
      };

      let branch = root;
      while (branch && branch !== document.body) {
        const parent = branch.parentElement;
        if (!parent) break;
        [...parent.children].forEach((element) => {
          if (
            element === branch ||
            element.matches('script, style, link, [data-vcc-review-overlay]')
          ) return;
          isolate(element);
        });
        branch = parent;
      }

      backgroundState = nextState;
    };

    const restoreBackground = () => {
      const releasedState = backgroundState;
      releasedState.forEach(({ element, inert, ariaHidden }) => {
        if (!element.isConnected) return;
        element.inert = inert;
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      backgroundState = [];
      window.VCCReviewViewer?.rebaseBackground?.(releasedState);
    };

    const openDrawer = (opener) => {
      window.clearTimeout(closeTimer);
      const generation = ++openGeneration;
      const wasOpen = root.classList.contains('is-open') && !root.hidden;
      lastOpener = opener || null;
      root.hidden = false;
      root.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('vcc-shared-trust-open');
      document.body?.classList.add('vcc-shared-trust-open');
      setExpanded(true);
      isolateBackground();

      if (!wasOpen) {
        const currentState = window.history.state;
        const nextState = currentState && typeof currentState === 'object' ? { ...currentState } : {};
        nextState[historyStateKey] = drawerId;
        window.history.pushState(nextState, '', window.location.href);
        ownsHistoryEntry = true;
      }

      window.requestAnimationFrame(() => {
        if (generation !== openGeneration || root.hidden || !root.isConnected) return;
        root.classList.add('is-open');
        window.requestAnimationFrame(() => {
          if (generation !== openGeneration || root.hidden || !root.isConnected || !root.classList.contains('is-open')) return;
          panel.querySelector('[data-vcc-shared-trust-close]')?.focus({ preventScroll: true });
        });
      });
    };

    const closeDrawer = ({ restoreHistory = true } = {}) => {
      if (root.hidden) return;
      openGeneration += 1;
      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('vcc-shared-trust-open');
      document.body?.classList.remove('vcc-shared-trust-open');
      setExpanded(false);
      restoreBackground();

      const opener = lastOpener;
      lastOpener = null;
      closeTimer = window.setTimeout(() => {
        if (!root.classList.contains('is-open')) root.hidden = true;
      }, transitionDuration);
      opener?.focus?.({ preventScroll: true });

      if (restoreHistory && ownsHistoryEntry && window.history.state?.[historyStateKey] === drawerId) {
        ownsHistoryEntry = false;
        window.history.back();
      } else if (!restoreHistory || window.history.state?.[historyStateKey] !== drawerId) {
        ownsHistoryEntry = false;
      }
    };

    document.addEventListener('click', (event) => {
      const opener = event.target.closest?.('[data-vcc-shared-trust-open]');
      if (!opener || opener.dataset.vccSharedTrustOpen !== drawerId) return;
      event.preventDefault();
      openDrawer(opener);
    });

    root.querySelectorAll('[data-vcc-shared-trust-close]').forEach((closeButton) => {
      closeButton.addEventListener('click', closeDrawer);
    });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeDrawer();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = focusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    window.addEventListener('popstate', (event) => {
      const destinationState = event.state && typeof event.state === 'object' ? event.state : {};
      const isActive = !root.hidden && root.getAttribute('aria-hidden') !== 'true';
      if (isActive && destinationState[historyStateKey] === drawerId) {
        ownsHistoryEntry = true;
        const generation = openGeneration;
        window.requestAnimationFrame(() => {
          if (generation !== openGeneration || root.hidden || !root.isConnected || !root.classList.contains('is-open')) return;
          root.querySelector('[data-vcc-open-reviews]')?.focus({ preventScroll: true });
        });
        return;
      }
      if (isActive) closeDrawer({ restoreHistory: false });
      ownsHistoryEntry = false;
    });
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-vcc-shared-trust-root]')) initDrawer(scope);
    scope.querySelectorAll?.('[data-vcc-shared-trust-root]').forEach(initDrawer);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initAll(node);
      });
    });
  });

  const observe = () => observer.observe(document.body, { childList: true, subtree: true });
  if (document.body) observe();
  else document.addEventListener('DOMContentLoaded', observe, { once: true });

  document.documentElement.dataset.vccSharedTrustReady = 'true';
  document.dispatchEvent(new CustomEvent('vcc:shared-trust:ready'));
})();
